import { memo } from 'react';
import { Platform, View, type ViewProps } from 'react-native';

import { createMqStyles } from '../../mq-styles';
import { tokens } from '../../tokens';
import { Text } from '../text';

export type SectionListHeaderProps = ViewProps & {
  title: string;
};

export const SectionListHeader = memo((props: SectionListHeaderProps) => {
  const mqStyles = useMqStyles();
  const { title, style, tabIndex: tabIndexProp, id, nativeID, ...rest } = props;
  const sectionDomId = nativeID ?? (typeof id === 'string' ? id : undefined);

  if (!title) {
    return (
      <View
        {...rest}
        nativeID={sectionDomId}
        accessible={false}
        accessibilityElementsHidden
        {...(Platform.OS === 'web'
          ? ({
              'data-fhs-empty-section-header': 'true',
              ...(sectionDomId ? { 'data-fhs-section-header': sectionDomId } : {}),
            } as const)
          : {})}
        style={[mqStyles.sectionContainer, mqStyles.empty, style]}
      />
    );
  }

  const webHeadingProps =
    Platform.OS === 'web'
      ? ({
          tabIndex: 0,
          role: 'heading',
          'aria-level': 2,
        } as Partial<ViewProps>)
      : { tabIndex: tabIndexProp ?? -1 };

  return (
    <View
      {...rest}
      nativeID={sectionDomId}
      accessible={true}
      accessibilityRole="header"
      accessibilityLabel={title}
      {...webHeadingProps}
      {...(Platform.OS === 'web' && sectionDomId
        ? ({ 'data-fhs-section-header': sectionDomId } as const)
        : {})}
      style={[mqStyles.sectionContainer, style]}
    >
      <Text.Heading accessible={false} importantForAccessibility="no" type="two">
        {title}
      </Text.Heading>
    </View>
  );
});

const useMqStyles = createMqStyles({
  empty: {
    $base: {
      height: 0,
      paddingTop: 0,
      marginTop: 0,
      marginBottom: 0,
    },
  },
  sectionContainer: {
    $base: {
      overflow: 'hidden',
      marginTop: 20,
      backgroundColor: tokens.colors.$white,
    },
    $gteDesktop: {
      marginTop: 44,
    },
  },
});
